import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useLeadsService from './useLeadsService';

// Chave usada para comunicação entre popup e aplicação principal
const FACEBOOK_INTEGRATION_RESULT_KEY = 'facebook_integration_result';

const useOpenPopupMetaLeads = (customerId: number) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { getCallbackMetaLeadsService, getUrlMetaLeadsService } =
    useLeadsService();

  // Função para verificar o status da autenticação
  const checkAuthStatus = useCallback(
    async (customerId: number) => {
      try {
        const currentUrl = window.location.href;

        const urlParams = new URLSearchParams(new URL(currentUrl).search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        if (code && state) {
          try {
            const res = await getCallbackMetaLeadsService(
              code,
              state,
              customerId
            );

            if (res.status === 200) {
              // Colocando o resultado no local storage para poder pegar na aplicação principal
              localStorage.setItem(
                FACEBOOK_INTEGRATION_RESULT_KEY,
                JSON.stringify({
                  success: true,
                  message: 'Integração com Facebook realizada com sucesso!',
                })
              );
            } else {
              // Colocando o resultado no local storage para poder pegar na aplicação principal
              localStorage.setItem(
                FACEBOOK_INTEGRATION_RESULT_KEY,
                JSON.stringify({
                  success: false,
                  message: 'Erro ao integrar com Facebook, contate o suporte.',
                })
              );
            }
          } catch (error) {
            console.error('Erro no callback do Facebook:', error);
            // Colocando o resultado no local storage para poder pegar na aplicação principal
            localStorage.setItem(
              FACEBOOK_INTEGRATION_RESULT_KEY,
              JSON.stringify({
                success: false,
                message: 'Erro ao finalizar integração com Facebook.',
              })
            );
          }

          // Fecha a popup e limpa o estado
          window.close();
        }
      } catch (error) {
        // Erros de CORS são esperados e podem ser ignorados
        // Ocorrem quando a popup navega para domínios diferentes
      }
    },
    [getCallbackMetaLeadsService]
  );

  // Função para verificar o resultado no localStorage
  const checkIntegrationResult = useCallback(() => {
    const resultStr = localStorage.getItem(FACEBOOK_INTEGRATION_RESULT_KEY);
    if (resultStr) {
      try {
        const result = JSON.parse(resultStr);
        if (result.success) {
          setIsConnected(true);
          toast.success(result.message);
        } else {
          setIsConnected(false);
          toast.error(result.message);
        }
      } catch (e) {
        console.error('Erro ao processar resultado da integração:', e);
      } finally {
        setIsLoading(false);
      }
      // Limpa o resultado após processá-lo
      localStorage.removeItem(FACEBOOK_INTEGRATION_RESULT_KEY);
    }
  }, []);

  const openPopupMetaLeads = useCallback(async () => {
    const width = 600;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // Limpa qualquer intervalo existente
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Limpa resultado anterior, se houver
    localStorage.removeItem(FACEBOOK_INTEGRATION_RESULT_KEY);

    try {
      setIsLoading(true);
      const url = await getUrlMetaLeadsService(customerId);

      const popupWindow = window.open(
        url,
        'Facebook',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popupWindow) {
        setIsLoading(false);
        toast.error(
          'Não foi possível abrir a popup. Verifique o bloqueador de popups.'
        );
        return;
      }

      // Configura o intervalo para verificar se a popup foi fechada
      intervalRef.current = setInterval(() => {
        // Verifica se a popup foi fechada pelo usuário
        if (popupWindow.closed) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          // Verifica se há um resultado no localStorage
          checkIntegrationResult();
          return;
        }

        // Também verifica se há um resultado no localStorage enquanto a popup está aberta
        // (caso a popup tenha fechado sem ser detectado corretamente)
        checkIntegrationResult();
      }, 1000);
    } catch (error) {
      console.error('Erro na integração com Facebook:', error);
      setIsLoading(false);
      toast.error('Erro ao obter URL do Facebook, contate o suporte.');
    } finally {
      setIsLoading(false);
    }
  }, [getUrlMetaLeadsService, customerId, checkIntegrationResult]);

  // Limpa recursos quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Este efeito só deve ser executado na página de callback do Facebook
  useEffect(() => {
    // Verifica se estamos na página de callback do Facebook
    const url = window.location.href;
    if (url.includes('code') && url.includes('state')) {
      if (customerId) {
        checkAuthStatus(customerId);
      }
    }
  }, [checkAuthStatus, window.location.href, customerId]);

  return { openPopupMetaLeads, isLoading, isConnected };
};

export default useOpenPopupMetaLeads;
